import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';
import { Plus, Search, Users, Globe } from 'lucide-react';
import { useHaptics } from '@/hooks/useHaptics';
import { isMobileDevice, cn } from '@/lib/utils';

interface AddRecipeActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddRecipeActionSheet: React.FC<AddRecipeActionSheetProps> = ({
  isOpen,
  onClose
}) => {
  const navigate = useNavigate();
  const { lightImpact, mediumImpact } = useHaptics();

  useEffect(() => {
    if (isOpen && isMobileDevice()) {
      mediumImpact(); // Haptic feedback on sheet appearance
    }
  }, [isOpen, mediumImpact]);

  const handleOptionSelect = (path: string) => {
    lightImpact();
    onClose();
    navigate(path);
  };

  const options = [
    {
      icon: Globe,
      title: 'Import from URL',
      description: 'Import recipes from recipe websites',
      path: '/import-recipes'
    },
    {
      icon: Plus,
      title: 'Add Quick Recipe',
      description: 'Create a simple recipe with minimal effort',
      path: '/create-quick-recipe'
    },
    {
      icon: Search,
      title: 'Find Recipe',
      description: 'Search for recipes online and save favorites',
      path: '/recipe-finder'
    },
    {
      icon: Users,
      title: 'Create Composition',
      description: 'Group multiple recipes together',
      path: '/create-composition'
    }
  ];

  return (
    <AdaptiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Recipe"
      position="bottom"
      size="md"
      scrollable={false}
    >
      <div className={cn(
        "space-y-2 mt-4",
        isMobileDevice() && "space-y-1"
      )}>
        {options.map((option) => (
          <button
            key={option.path}
            onClick={() => handleOptionSelect(option.path)}
            tabIndex={-1}
            className={cn(
              "w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted/50 transition-colors text-left",
              "focus:outline-none focus:ring-2 focus:ring-sage/50",
              isMobileDevice() && [
                "rounded-xl p-3",
                "active:scale-95 transition-all duration-150"
              ]
            )}
          >
            <div className={cn(
              "w-10 h-10 bg-sage/10 rounded-full flex items-center justify-center",
              isMobileDevice() && "w-12 h-12"
            )}>
              <option.icon className="h-5 w-5 text-sage" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium">{option.title}</h3>
              <p className="text-sm text-muted-foreground">{option.description}</p>
            </div>
          </button>
        ))}
      </div>
    </AdaptiveModal>
  );
};

export default AddRecipeActionSheet;
